import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDetailLinkState } from '../../../utils/navigationState';
import inventoryApi from '../../../services/inventoryApi';
import adminAPI from '../../../services/adminApi';
import { useAuth } from '../../../contexts/AuthContext';
import PermissionGuard from '../../Common/PermissionGuard';

import PurchaseOrderDetails from './PurchaseOrderDetails';
import PurchaseOrderForm from './PurchaseOrderForm';
import ReceiveOrderDialog from './ReceiveOrderDialog';
import LoadingSpinner from '../../Common/LoadingSpinner';
import { openPurchaseOrderReport } from '../../../utils/openInventoryReport';
import { canPrintPurchaseOrder } from '../../../utils/documentPrintGate';

const PurchaseOrderManagement = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search and filters - matching Customer list pattern
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [debouncedSearchInput, setDebouncedSearchInput] = useState('');

  // Dialog states
  const [openForm, setOpenForm] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [openReceive, setOpenReceive] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [presetPRId, setPresetPRId] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setCompanyInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[PurchaseOrderManagement] could not load location info:', e.message);
      }
    })();
  }, []);

  const handlePrintOrder = async (order) => {
    await openPurchaseOrderReport(order, companyInfo);
  };

  // View state - matching Customer list pattern
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('purchaseOrderListView') || 'grid';
    } catch {
      return 'grid';
    }
  });

  // Pagination - matching Supplier list pattern
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Suppliers for filter
  const [suppliers, setSuppliers] = useState([]);

  // Debounce search input - matching Customer list pattern
  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      setDebouncedSearchInput(searchInput);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [searchInput]);

  useEffect(() => {
    loadPurchaseOrders();
    loadSuppliers();
  }, [debouncedSearchInput, statusFilter, supplierFilter, priorityFilter]);

  const loadPurchaseOrders = async () => {
    // Check read permission before making API call
    if (!hasPermission('INVENTORY:PURCHASE_ORDERS:READ')) {
      setError('You do not have permission to view purchase orders');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = {};

      if (debouncedSearchInput) params.search = debouncedSearchInput;
      if (statusFilter) params.status = statusFilter;
      if (supplierFilter) params.supplier = supplierFilter;
      if (priorityFilter) params.priority = priorityFilter;

      const response = await inventoryApi.getPurchaseOrders(params);
      setPurchaseOrders(response.data || []);

      setError('');
    } catch (error) {
      console.error('Error loading purchase orders:', error);
      setError('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await inventoryApi.getSuppliers({ limit: 1000 });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  // Filtered orders computed value - matching Customer list pattern
  const filteredOrders = useMemo(() => {
    return purchaseOrders.filter(order => {
      const matchesSearch = !debouncedSearchInput ||
        order.orderNumber?.toLowerCase().includes(debouncedSearchInput.toLowerCase()) ||
        order.supplier?.name?.toLowerCase().includes(debouncedSearchInput.toLowerCase()) ||
        order.description?.toLowerCase().includes(debouncedSearchInput.toLowerCase());

      const matchesStatus = !statusFilter || order.status === statusFilter;
      const matchesSupplier = !supplierFilter || order.supplier?.name === supplierFilter;
      const matchesPriority = !priorityFilter || order.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesSupplier && matchesPriority;
    });
  }, [purchaseOrders, debouncedSearchInput, statusFilter, supplierFilter, priorityFilter]);

  // Pagination calculations - matching Supplier list pattern
  const totalItems = filteredOrders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchInput, statusFilter, supplierFilter, priorityFilter]);

  const handleCreateOrder = () => {
    if (!hasPermission('INVENTORY:PURCHASE_ORDERS:CREATE')) {
      setError('You do not have permission to create purchase orders');
      return;
    }
    setSelectedOrder(null);
    setIsEditing(false);
    setPresetPRId('');
    setOpenForm(true);
  };

  // Handle incoming purchase request from navigation state (Convert to PO from the PR view).
  // The form loads the request itself, so only the id travels here.
  useEffect(() => {
    if (!location.state?.selectedPRId || !location.state?.fromPR) return;

    if (!hasPermission('INVENTORY:PURCHASE_ORDERS:CREATE')) {
      setError('You do not have permission to create purchase orders');
    } else {
      setSelectedOrder(null);
      setIsEditing(false);
      setPresetPRId(location.state.selectedPRId);
      setOpenForm(true);
    }

    // Clear the payload so a refresh or back-navigation does not reopen the form
    navigate(`${location.pathname}${location.search || ''}`, {
      replace: true,
      state: { from: location.state?.from, fromTab: location.state?.fromTab }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname, location.search, navigate]);

  const handleEditOrder = (order) => {
    if (!hasPermission('INVENTORY:PURCHASE_ORDERS:UPDATE')) {
      setError('You do not have permission to edit purchase orders');
      return;
    }
    setSelectedOrder(order);
    setIsEditing(true);
    setOpenForm(true);
  };

  const handleViewOrder = (order) => {
    if (!hasPermission('INVENTORY:PURCHASE_ORDERS:READ')) {
      setError('You do not have permission to view purchase order details');
      return;
    }
    setSelectedOrder(order);
    setOpenDetails(true);
  };

  const handleReceiveOrder = (order) => {
    if (!hasPermission('INVENTORY:PURCHASE_ORDERS:RECEIVE')) {
      setError('You do not have permission to receive purchase orders');
      return;
    }
    setSelectedOrder(order);
    setOpenReceive(true);
  };

  const handleConvertToGRN = (order) => {
    if (!hasPermission('INVENTORY:PURCHASE_ORDERS:RECEIVE')) {
      setError('You do not have permission to convert purchase orders to GRN');
      return;
    }
    // Navigate to GRN page with the selected PO data
    navigate('/inventory/grn', { 
      state: { 
        ...getDetailLinkState(location),
        selectedPO: order,
        fromPO: true 
      } 
    });
  };

  const handleDeleteOrder = async (orderId) => {
    if (!hasPermission('INVENTORY:PURCHASE_ORDERS:DELETE')) {
      setError('You do not have permission to delete purchase orders');
      return;
    }
    try {
      await inventoryApi.deletePurchaseOrder(orderId);
      setSuccess('Purchase order deleted successfully');
      setShowDeleteConfirm(null);
      loadPurchaseOrders();
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      setError('Failed to delete purchase order');
      setShowDeleteConfirm(null);
    }
  };

  const handleApproveOrder = async (order) => {
    if (!hasPermission('INVENTORY:PURCHASE_ORDERS:APPROVE')) {
      setError('You do not have permission to approve purchase orders');
      return;
    }
    if (window.confirm('Are you sure you want to approve this purchase order?')) {
      try {
        await inventoryApi.approvePurchaseOrder(order._id, { approved: true });
        setSuccess('Purchase order approved successfully');
        loadPurchaseOrders();
      } catch (error) {
        console.error('Error approving purchase order:', error);
        // Surface the server's reason — it explains approval-level rejections.
        setError(error.response?.data?.message || 'Failed to approve purchase order');
      }
    }
  };

  const handleRejectOrder = async (order) => {
    if (!hasPermission('INVENTORY:PURCHASE_ORDERS:APPROVE')) {
      setError('You do not have permission to reject purchase orders');
      return;
    }
    const reason = prompt('Please provide a reason for rejection:');
    if (reason !== null) {
      try {
        await inventoryApi.approvePurchaseOrder(order._id, {
          approved: false,
          comments: reason
        });
        setSuccess('Purchase order rejected successfully');
        loadPurchaseOrders();
      } catch (error) {
        console.error('Error rejecting purchase order:', error);
        setError(error.response?.data?.message || 'Failed to reject purchase order');
      }
    }
  };

  const handleRefresh = async () => {
    await loadPurchaseOrders();
  };

  const handleFormSubmit = async (orderData) => {
    try {
      if (isEditing) {
        await inventoryApi.updatePurchaseOrder(selectedOrder._id, orderData);
        setSuccess('Purchase order updated successfully');
      } else {
        await inventoryApi.createPurchaseOrder(orderData);
        setSuccess('Purchase order created successfully');
      }
      setOpenForm(false);
      setPresetPRId('');
      loadPurchaseOrders();
    } catch (error) {
      console.error('Error saving purchase order:', error);
      setError('Failed to save purchase order');
    }
  };

  const handleReceiveSubmit = async (receiptData) => {
    try {
      await inventoryApi.receivePurchaseOrder(selectedOrder._id, receiptData);
      setSuccess('Items received successfully');
      setOpenReceive(false);
      loadPurchaseOrders();
    } catch (error) {
      console.error('Error receiving items:', error);
      setError('Failed to receive items');
    }
  };

  // Helper functions - matching Customer list pattern
  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('purchaseOrderListView', newView);
    } catch (error) {
      console.warn('Failed to save view preference:', error);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setStatusFilter('');
    setSupplierFilter('');
    setPriorityFilter('');
    setCurrentPage(1); // Reset to first page
  };

  const hasFilters = () => searchInput || statusFilter || supplierFilter || priorityFilter;

  // Calculate pending quantities for an order
  const calculatePendingQuantities = (order) => {
    if (!order.lineItems || order.lineItems.length === 0) {
      return { totalOrdered: 0, totalReceived: 0, totalPending: 0, hasPending: false };
    }
    
    const totalOrdered = order.lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalReceived = order.lineItems.reduce((sum, item) => sum + (item.receivedQuantity || 0), 0);
    const totalPending = totalOrdered - totalReceived;
    
    return {
      totalOrdered,
      totalReceived,
      totalPending,
      hasPending: totalPending > 0,
      percentageReceived: totalOrdered > 0 ? ((totalReceived / totalOrdered) * 100).toFixed(1) : 0
    };
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-blue-100 text-blue-800 border-blue-200',
      sent: 'bg-purple-100 text-purple-800 border-purple-200',
      partially_received: 'bg-orange-100 text-orange-800 border-orange-200',
      received: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityBadgeColor = (priority) => {
    const colors = {
      low: 'bg-green-100 text-green-800 border-green-200',
      normal: 'bg-gray-100 text-gray-800 border-gray-200',
      high: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      urgent: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Render functions for different views
  const renderOrderCard = (order) => (
    <div key={order._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-32 w-full">
      <div className="p-2.5 h-full flex flex-col">
        <div className="flex items-start justify-between mb-1.5">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
              {order.status?.charAt(0)?.toUpperCase() || 'P'}
            </div>
            <div className="min-w-0 flex-1">
              <button
                onClick={() => handleViewOrder(order)}
                className="text-sm font-semibold text-gray-900 hover:text-indigo-600 transition-colors truncate block"
              >
                {order.orderNumber || `PO-${order._id?.slice(-6)?.toUpperCase()}`}
              </button>
              <p className="text-xs text-gray-500 truncate">{order.supplier?.name}</p>
            </div>
          </div>

          <div className="ml-2 flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
            <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:READ">
              <button
                onClick={() => handleViewOrder(order)}
                className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                title="View order"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            </PermissionGuard>
            {canPrintPurchaseOrder(order.status) && (
              <button
                onClick={() => handlePrintOrder(order)}
                className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                title="Print PDF"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
              </button>
            )}
            {['approved', 'sent_to_supplier'].includes(order.status) && (
              <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:RECEIVE">
                <button
                  onClick={() => handleConvertToGRN(order)}
                  className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                  title="Convert to GRN"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </button>
              </PermissionGuard>
            )}
            {!['approved', 'sent', 'partially_received', 'received', 'cancelled'].includes(order.status) && (
              <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:UPDATE">
                <button
                  onClick={() => handleEditOrder(order)}
                  className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                  title="Edit order"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </PermissionGuard>
            )}
            {(order.status === 'draft' || order.status === 'pending') && (
              <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:DELETE">
                <button
                  onClick={() => setShowDeleteConfirm(order)}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                  title="Delete order"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </PermissionGuard>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="text-xs text-gray-600 mb-1.5 flex-1">
          <div className="flex items-center justify-between">
            {order.totals?.grandTotal && (
              <span className="text-gray-700 font-medium">{order.totals.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            )}
            {order.expectedDeliveryDate && (
              <span className="text-gray-500">{new Date(order.expectedDeliveryDate).toLocaleDateString()}</span>
            )}
          </div>
          {order.warehouse?.name && (
            <p className="text-gray-500 truncate mt-0.5">{order.warehouse.name}</p>
          )}
          {/* Balance Quantity Indicator */}
          {(() => {
            const pending = calculatePendingQuantities(order);
            if (pending.hasPending && ['approved', 'sent_to_supplier'].includes(order.status)) {
              return (
                <div className="flex items-center gap-1 mt-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 rounded-full transition-all"
                      style={{ width: `${pending.percentageReceived}%` }}
                    />
                  </div>
                  <span className="text-xs text-orange-600 font-medium whitespace-nowrap">
                    {pending.totalReceived}/{pending.totalOrdered}
                  </span>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* Badges */}
        <div className="flex items-center justify-between mt-auto">
          <div className="flex flex-wrap gap-1">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(order.status)}`}>
              {order.status?.toUpperCase()}
            </span>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getPriorityBadgeColor(order.priority)}`}>
              {order.priority?.charAt(0)?.toUpperCase() || 'N'}
            </span>
          </div>
          {order.lineItems?.length && (
            <span className="text-xs text-gray-500">
              {order.lineItems.length} items
            </span>
          )}
        </div>
      </div>
    </div>
  );





  if (loading && purchaseOrders.length === 0) {
       return <LoadingSpinner />;
  }

  return (
    <PermissionGuard
      permission="INVENTORY:PURCHASE_ORDERS:READ"
      fallback={
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 0h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-sm text-gray-500">You don't have permission to view purchase orders.</p>
          </div>
        </div>
      }
    >
      <div className="bg-white shadow-sm border border-gray-200">
          {/* Alerts */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium text-red-800">{error}</span>
                <button
                  onClick={() => setError('')}
                  className="ml-auto text-red-600 hover:text-red-800"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded p-3">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium text-green-800">{success}</span>
                <button
                  onClick={() => setSuccess('')}
                  className="ml-auto text-green-600 hover:text-green-800"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="bg-white shadow-sm border border-gray-200">
            {/* Filters and Search */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-2">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="block w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                    placeholder="Search purchase orders by number, supplier, description..."
                  />
                </div>
              </div>

              {/* Filters and Actions */}
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[120px]"
                >
                  <option value="">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="sent">Sent</option>
                  <option value="partially_received">Partially Received</option>
                  <option value="received">Received</option>
                  <option value="cancelled">Cancelled</option>
                </select>

                {/* Supplier Filter */}
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[150px]"
                >
                  <option value="">All Suppliers</option>
                  {suppliers.map(supplier => (
                    <option key={supplier._id} value={supplier._id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>

                {/* Priority Filter */}
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[120px]"
                >
                  <option value="">All Priority</option>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>

                {/* View Toggle */}
                <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                  <button
                    onClick={() => handleViewChange('grid')}
                    className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'grid'
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    title="Card view"
                  >
                    <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                    </svg>
                    Cards
                  </button>
                  <button
                    onClick={() => handleViewChange('list')}
                    className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list'
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    title="List view"
                  >
                    <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    List
                  </button>
                </div>

                {/* Add Purchase Order Button */}
                <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:CREATE">
                  <button
                    onClick={handleCreateOrder}
                    className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                  >
                    <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Purchase Order
                  </button>
                </PermissionGuard>
              </div>
            </div>
          </div>
          {/* Pagination */}
          {filteredOrders.length > 0 && (
            <div className="flex items-center justify-between pb-2 border-b  px-2  py-2 bg-gradient-to-r from-gray-100 to-gray-100 mb-6 border-b border-gray-200">
              {/* Results Info */}
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} orders
                  </p>
                  <p className="text-xs text-gray-500">
                    {hasFilters() ? 'Filtered results' : 'All orders'}
                  </p>
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center gap-3">
                {/* Items per page */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">Show:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                    disabled={loading}
                    className="px-2 py-1 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[70px]"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage <= 1 || loading}
                    className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title="First page"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                  </button>

                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1 || loading}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span className="hidden sm:inline">Previous</span>
                  </button>

                  {/* Page Numbers */}
                  <div className="hidden md:flex items-center gap-1">
                    {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      const isCurrentPage = pageNum === currentPage;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${isCurrentPage
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    {totalPages > 7 && (
                      <span className="px-2 text-gray-400 text-sm">...</span>
                    )}
                  </div>

                  {/* Mobile page indicator */}
                  <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                    <span className="text-sm font-medium text-gray-900">{currentPage}</span>
                    <span className="text-sm text-gray-500 mx-1">of</span>
                    <span className="text-sm font-medium text-gray-900">{totalPages}</span>
                  </div>

                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages || loading}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage >= totalPages || loading}
                    className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title="Last page"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-2 bg-white">
            {filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No purchase orders found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {hasFilters() ? 'Try adjusting your search or filter criteria.' : 'Get started by creating your first purchase order.'}
                </p>
                {!hasFilters() && (
                  <div className="mt-6">
                    <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:CREATE">
                      <button
                        onClick={handleCreateOrder}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create Purchase Order
                      </button>
                    </PermissionGuard>
                  </div>
                )}
              </div>
            ) : (
              <>

                {/* Grid View */}
                {view === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-3">
                    {paginatedOrders.map(order => renderOrderCard(order))}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-max w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Warehouse</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedOrders.map(order => (
                          <tr key={order._id} className="hover:bg-gray-50">
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="font-mono text-sm text-gray-900 font-medium">
                                {order.orderNumber || `PO-${order._id?.slice(-6)?.toUpperCase()}`}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(order.createdAt).toLocaleDateString()}
                              </div>
                              {(order.metadata?.createdByName || order.metadata?.createdBy?.name) && (
                                <div className="text-xs text-gray-400">
                                  By: {order.metadata?.createdByName || order.metadata?.createdBy?.name}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="font-medium text-gray-900">
                                {order.supplier?.name || 'N/A'}
                              </div>
                              {order.supplier?.contactInfo?.email && (
                                <div className="text-sm text-gray-500 truncate max-w-xs">
                                  {order.supplier.contactInfo.email}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="font-medium text-gray-900">
                                {order.warehouse?.name || 'N/A'}
                              </div>
                              {order.warehouse?.code && (
                                <div className="text-gray-500 font-mono text-xs">
                                  {order.warehouse.code} ({order.warehouse.type})
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="space-y-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusBadgeColor(order.status)}`}>
                                  {order.status?.toUpperCase()}
                                </span>
                                <br />
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getPriorityBadgeColor(order.priority)}`}>
                                  {order.priority?.toUpperCase() || 'NORMAL'}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              {(() => {
                                const pending = calculatePendingQuantities(order);
                                return (
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {pending.totalReceived} / {pending.totalOrdered}
                                    </div>
                                    {pending.hasPending && ['approved', 'sent_to_supplier'].includes(order.status) && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <div className="flex-1 max-w-[60px] bg-gray-200 rounded-full h-1.5">
                                          <div 
                                            className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 rounded-full"
                                            style={{ width: `${pending.percentageReceived}%` }}
                                          />
                                        </div>
                                        <span className="text-xs text-orange-600 font-medium">
                                          {pending.totalPending} pending
                                        </span>
                                      </div>
                                    )}
                                    {!pending.hasPending && order.status === 'received' && (
                                      <span className="text-xs text-green-600 font-medium">Complete</span>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {order.totals?.grandTotal?.toLocaleString() || '0.00'}
                              </div>
                              {order.lineItems?.length && (
                                <div className="text-xs text-gray-500">
                                  {order.lineItems.length} items
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {new Date(order.createdAt).toLocaleDateString()}
                              </div>
                              {order.expectedDeliveryDate && (
                                <div className="text-xs text-gray-500">
                                  Expected: {new Date(order.expectedDeliveryDate).toLocaleDateString()}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:READ">
                                  <button
                                    onClick={() => handleViewOrder(order)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                    title="View Details"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  </button>
                                </PermissionGuard>
                                {canPrintPurchaseOrder(order.status) && (
                                  <button
                                    onClick={() => handlePrintOrder(order)}
                                    className="text-gray-600 hover:text-gray-900"
                                    title="Print PDF"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                    </svg>
                                  </button>
                                )}
                                {!['approved', 'sent', 'partially_received', 'received', 'cancelled'].includes(order.status) && (
                                  <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:UPDATE">
                                    <button
                                      onClick={() => handleEditOrder(order)}
                                      className="text-emerald-600 hover:text-emerald-900"
                                      title="Edit"
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                  </PermissionGuard>
                                )}
                                {/* TODO: dead condition — the controller never writes 'pending'
                                    (a draft PO awaiting approval has status 'draft'), so these
                                    buttons never render. Left as-is deliberately: showing Approve
                                    here without knowing the order's approval level would offer it
                                    to users the server will reject. Approve/Reject live in the
                                    details modal, which resolves the level first. */}
                                {order.status === 'pending' && (
                                  <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:APPROVE">
                                    <button
                                      onClick={() => handleApproveOrder(order)}
                                      className="text-green-600 hover:text-green-900"
                                      title="Approve"
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    </button>
                                  </PermissionGuard>
                                )}
                                {['approved', 'sent_to_supplier'].includes(order.status) && (
                                  <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:RECEIVE">
                                    <button
                                      onClick={() => handleConvertToGRN(order)}
                                      className="text-blue-600 hover:text-blue-900"
                                      title="Convert to GRN"
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                      </svg>
                                    </button>
                                  </PermissionGuard>
                                )}
                                {(order.status === 'sent' || order.status === 'partially_received') && (
                                  <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:RECEIVE">
                                    <button
                                      onClick={() => handleReceiveOrder(order)}
                                      className="text-purple-600 hover:text-purple-900"
                                      title="Receive Order"
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                      </svg>
                                    </button>
                                  </PermissionGuard>
                                )}
                                {(order.status === 'draft' || order.status === 'pending') && (
                                  <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:DELETE">
                                    <button
                                      onClick={() => setShowDeleteConfirm(order)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete"
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </PermissionGuard>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>

        {
          openForm && (
            <PurchaseOrderForm
              open={openForm}
              onClose={() => { setOpenForm(false); setPresetPRId(''); }}
              onSubmit={handleFormSubmit}
              initialData={selectedOrder}
              isEditing={isEditing}
              presetPurchaseRequestId={presetPRId}
            />
          )
        }

        {
          openDetails && (
            <PurchaseOrderDetails
              open={openDetails}
              onClose={() => setOpenDetails(false)}
              order={selectedOrder}
              onOrderUpdate={() => {
                loadPurchaseOrders();
              }}
            />
          )
        }

        {
          openReceive && (
            <ReceiveOrderDialog
              open={openReceive}
              onClose={() => setOpenReceive(false)}
              onSubmit={handleReceiveSubmit}
              order={selectedOrder}
            />
          )
        }

        {/* Delete Confirmation Dialog */}
        {
          showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-md w-full p-6">
                <h3 className="text-lg font-semibold mb-2">Delete Purchase Order</h3>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to delete purchase order "{showDeleteConfirm.orderNumber || `PO-${showDeleteConfirm._id?.slice(-6)?.toUpperCase()}`}"?
                  This action cannot be undone and will permanently remove all order data.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteOrder(showDeleteConfirm._id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )
        }
      </div>
    </PermissionGuard>
  );
};

export default PurchaseOrderManagement;